import { generateHeightmap, smoothHeightmap, makeNoise2D } from "./heightmap";
import { applyTalusErosion } from "./erosion";
import { addPonds, carveRiverWithBanks, carveWaterfalls, generateRiverMask } from "./rivers";
import { placeNetherPortal } from "./portal";
import { placeNetherStructure } from "./structures";
import { hashSeed } from "./biomes";
import { createWorld } from "./terrainVoxels";
import { BLOCK } from "../types";
import {
    GRID_SIZE,
    WORLD_HEIGHT,
    BASE_GROUND_OFFSET,
    BEDROCK_Y,
    SMOOTHING_PASSES,
    NETHER_SEED_OFFSET,
    NETHER_LAVA_HALF_THICKNESS,
    NETHER_GLOWSTONE_CLUSTERS,
} from "../config";

export interface BuildNetherOptions {
    size?: number;
    height?: number;
}

export function buildNether(seed: number, options: BuildNetherOptions = {}) {
    const size = options.size ?? GRID_SIZE;
    const worldHeight = options.height ?? WORLD_HEIGHT;
    const s = seed + NETHER_SEED_OFFSET;

    let heights = generateHeightmap(s, size);
    heights = applyTalusErosion(heights);
    heights = smoothHeightmap(heights, SMOOTHING_PASSES);

    const lavaMask = generateRiverMask(s, size, NETHER_LAVA_HALF_THICKNESS);
    addPonds(lavaMask, s, size, 0.3);
    heights = carveRiverWithBanks(heights, lavaMask);

    const world = createWorld(size, worldHeight);

    const glowNoise = makeNoise2D(s + 313);
    for (let x = 0; x < size; x++) {
        for (let z = 0; z < size; z++) {
            const topAbs = BASE_GROUND_OFFSET + heights[x][z];
            world.set(x, BEDROCK_Y, z, BLOCK.Bedrock);
            world.fillColumn(x, z, BEDROCK_Y + 1, topAbs, BLOCK.Netherrack);

            if (lavaMask[x][z]) {
                world.set(x, topAbs + 1, z, BLOCK.Lava);
            } else if ((glowNoise(x * 0.9, z * 0.9) + 1) / 2 > 0.965) {
                world.set(x, topAbs, z, BLOCK.Glowstone);
            }
        }
    }

    carveWaterfalls(world, heights, lavaMask, BLOCK.Lava);

    for (let k = 0; k < NETHER_GLOWSTONE_CLUSTERS; k++) {
        const cx = 4 + (hashSeed(s + 1000 + k) % (size - 8));
        const cz = 4 + (hashSeed(s + 2000 + k) % (size - 8));
        if (lavaMask[cx][cz]) continue;
        const y = BASE_GROUND_OFFSET + heights[cx][cz] + 5 + (hashSeed(s + 3000 + k) % 3);
        world.set(cx, y, cz, BLOCK.Glowstone);
        world.set(cx + 1, y, cz, BLOCK.Glowstone);
        world.set(cx, y, cz + 1, BLOCK.Glowstone);
        world.set(cx, y - 1, cz, BLOCK.Glowstone);
    }

    const structure = placeNetherStructure(world, heights, seed);
    placeNetherPortal(world, heights, seed);

    return { world, heights, structure };
}
